import React, { createContext, useContext, useEffect, useState } from 'react';
import { useShowContext } from 'framework/show';
import useVendorService from '../../useVendorService';
import {
  FINANCE_VENDOR_AGREEMENT_FINALIZE,
  FINANCE_VENDOR_AGREEMENT_TERMINATE,
  FINANCE_VENDOR_AGREEMENT_UPDATE,
} from '../../constants';

type Permission = string;

interface AgreementPermissionsContextValue {
  isLoading: boolean;
  hasPermission: (permission: Permission) => boolean;
}

const AgreementPermissionsContext = createContext<AgreementPermissionsContextValue | undefined>(undefined);

interface AgreementPermissionsProviderProps {
  children: React.ReactNode;
}

export const AgreementPermissionsProvider = ({ children }: AgreementPermissionsProviderProps) => {
  const { data: agreement } = useShowContext();
  const { getAgreementPermissions } = useVendorService();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!agreement?.id) {
        if (isMounted) {
          setPermissions([]);
          setIsLoading(false);
        }
        return;
      }

      try {
        const loadedPermissions = await getAgreementPermissions(agreement.id);

        if (isMounted) {
          setPermissions(loadedPermissions);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement?.id]);

  const hasPermission = (permission: Permission) => permissions.includes(permission);

  return (
    <AgreementPermissionsContext.Provider
      value={{
        isLoading,
        hasPermission,
      }}
    >
      {children}
    </AgreementPermissionsContext.Provider>
  );
};

export const useAgreementPermissions = () => {
  const context = useContext(AgreementPermissionsContext);

  if (!context) {
    throw new Error('useAgreementPermissions must be used within AgreementPermissionsProvider');
  }

  return context;
};

export const AGREEMENT_PERMISSIONS = {
  FINALIZE: FINANCE_VENDOR_AGREEMENT_FINALIZE,
  UPDATE: FINANCE_VENDOR_AGREEMENT_UPDATE,
  TERMINATE: FINANCE_VENDOR_AGREEMENT_TERMINATE,
} as const;
